import { useTranslation } from "react-i18next";
import { type FunctionComponent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { useForm, type SubmitHandler } from "react-hook-form";
import { ForgotPasswordSchema } from "@/schemas/forgotPassword.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";

import { Form, FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { useAuthStore } from "@/store/auth.store";
import { Mail } from "lucide-react";
import { FormField } from "@/components/ui/form/FormField";
import { Input } from "@/components/ui/input";

type ForgotPasswordForm = z.infer<typeof ForgotPasswordSchema>;

export const ForgotPassword: FunctionComponent = () => {
  const { t } = useTranslation();
  const { forgotPassword, loading } = useAuthStore.getState();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordSchema),
    shouldFocusError: true,
    defaultValues: {
      email: "",
    },
  });

  const {
    formState: { isValid },
  } = form;

  const onSubmit: SubmitHandler<ForgotPasswordForm> = async (values) => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      await forgotPassword(values.email);
      setSuccessMessage(
        t("forgotPassword.messages.success", { email: values.email })
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error_) {
      setErrorMessage(t("forgotPassword.messages.error"));
    }
  };

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <div className="w-full flex flex-col max-w-[450px] px-4 gap-4">
        <h1 className="text-[2rem] font-bold text-primary-dark dark:text-foreground text-center">
          {t("forgotPassword.title")}
        </h1>
        {successMessage ? (
          <div className="w-full text-center">
            <div className="mb-4 text-success">{successMessage}</div>
            <Link className="font-medium text-primary hover:underline" to="/">
              {t("forgotPassword.backTo")} {t("forgotPassword.loginLink")}
            </Link>
          </div>
        ) : (
          <>
            <div className="w-full mb-8">
              <p className="text-tinted-grey-4 text-xs font-normal">
                {t("forgotPassword.description")}
              </p>
            </div>
            <Form {...form}>
              <form
                noValidate
                className="w-full space-y-4"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full flex flex-col space-y-1">
                      <FormControl>
                        <Input
                          icon={<Mail />}
                          placeholder={t("forgotPassword.form.emailLabel")}
                          {...field}
                        />
                      </FormControl>
                      {errorMessage && (
                        <div className="mt-1 text-xs text-error text-center">
                          {errorMessage}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="w-full"
                  data-testid="forgot-password-submit-button"
                  disabled={!isValid || loading}
                  size="xl"
                  type="submit"
                >
                  {loading
                    ? t("forgotPassword.form.loadingButton")
                    : t("forgotPassword.form.resetButton")}
                </Button>
              </form>
            </Form>
          </>
        )}

        {!successMessage && (
          <p className="mt-4 text-sm text-tinted-grey-3 text-center">
            {t("forgotPassword.backToLogin")}{" "}
            <Link className="font-medium text-primary hover:underline" to="/">
              {t("forgotPassword.backTo")} {t("forgotPassword.loginLink")}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};
